import type { MagazineArticle } from '@titularis/api-client';

/**
 * A referência nasce dos campos publicados, nos dois leitores da página.
 * A data de acesso vem de quem chama: consultar o relógio aqui faria a mesma
 * entrada produzir textos diferentes e impediria conferir a citação inteira.
 */
export function separarNome(nome: string): { nome: string; sobrenome: string } {
  const palavras = nome.trim().split(/\s+/).filter(Boolean);
  let inicio = Math.max(0, palavras.length - 1);
  // A geração acompanha o sobrenome; as partículas ficam no fim do prenome.
  if (inicio > 0 && /^(filho|neto|j[uú]nior|sobrinho)$/i.test(palavras[inicio]!)) inicio -= 1;
  while (inicio < palavras.length - 1 && /^(de|da|do|das|dos|e)$/i.test(palavras[inicio]!)) {
    inicio += 1;
  }
  return { nome: palavras.slice(0, inicio).join(' '), sobrenome: palavras.slice(inicio).join(' ') };
}

const nomeInvertido = (nome: string, maiusculas = false): string => {
  const partes = separarNome(nome);
  const sobrenome = maiusculas ? partes.sobrenome.toLocaleUpperCase('pt-BR') : partes.sobrenome;
  return `${sobrenome}${partes.nome ? `, ${partes.nome}` : ''}`;
};

export const tituloDoArtigo = (artigo: Pick<MagazineArticle, 'title' | 'subtitle'>): string =>
  [artigo.title, artigo.subtitle].filter(Boolean).join(': ');

// Quebras e comandos do editor não podem criar campos extras em RIS ou fechar chaves do BibTeX.
const linha = (texto: string): string => texto.replace(/\s+/g, ' ').trim();
const bib = (texto: string): string => linha(texto).replace(/[\\{}%&#_$~^]/g, (caractere) => {
  if (caractere === '\\') return '\\textbackslash{}';
  if (caractere === '~') return '\\textasciitilde{}';
  if (caractere === '^') return '\\textasciicircum{}';
  return `\\${caractere}`;
});

export function citarArtigo(artigo: MagazineArticle, url: string, acesso: Date) {
  const titulo = tituloDoArtigo(artigo);
  const { volume, number, year } = artigo.issue;
  const data = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Sao_Paulo',
  }).formatToParts(acesso);
  const parte = (tipo: Intl.DateTimeFormatPartTypes) => data.find((item) => item.type === tipo)?.value;
  const chave = artigo.slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9_-]/g, '') || `titularis${year}`;

  return {
    ABNT: `${artigo.authors.map((autor) => nomeInvertido(autor.name, true)).join('; ')}. ${titulo}. Revista Titularis, v. ${volume}, n. ${number}, ${year}. Disponível em: ${url}. Acesso em: ${parte('day')} ${parte('month')} ${parte('year')}.`,
    BibTeX: `@article{${chave},\n  author = {${artigo.authors.map((autor) => bib(nomeInvertido(autor.name))).join(' and ')}},\n  title = {${bib(titulo)}},\n  journal = {Revista Titularis},\n  volume = {${volume}},\n  number = {${number}},\n  year = {${year}},\n  url = {${bib(url)}}\n}`,
    RIS: [
      'TY  - JOUR',
      ...artigo.authors.map((autor) => `AU  - ${linha(nomeInvertido(autor.name))}`),
      `TI  - ${linha(titulo)}`, 'T2  - Revista Titularis', `VL  - ${volume}`,
      `IS  - ${number}`, `PY  - ${year}`, `UR  - ${linha(url)}`, 'ER  -',
    ].join('\n'),
  };
}
